import { useCallback } from "react";

import { useLeagueNaming } from "@/contexts";
import { BasesHavingAll, MessageKey } from "@/i18n";

/**
 * Resolves a tournament-term message key to the naming leaf that fits the active
 * context. Every string that renames with the tournament term is nested under two
 * reserved marker leaves in the locale files:
 *   - `_tournament` — the default, and the leaf platform terminology overrides
 *     (race/event) rename; picked outside a league (tour) context.
 *   - `_stage` — the wording used when a `league` naming context is active, where
 *     a tournament is a "stage" of the league.
 * The underscore keeps each marker unambiguous (no content key collides with it),
 * which is what the `i18n:check` coverage check keys on. The active
 * `LeagueNamingContext` decides which leaf to append.
 */
export const useAddTournamentNamingKey = () => {
  const naming = useLeagueNaming();

  return useCallback(
    <TMessageKey extends BasesHavingAll<MessageKey, "_tournament" | "_stage">>(
      messageKey: TMessageKey
    ): `${TMessageKey}._tournament` | `${TMessageKey}._stage` =>
      naming === "league" ? `${messageKey}._stage` : `${messageKey}._tournament`,
    [naming]
  );
};
